import { useEffect, useState } from "react";
import Modal from "react-modal";
import { CircleX, EyeOff, Eye } from "lucide-react";
import { postData, putData } from "../../services/Axios/Axios";
import messageService from "../../services/message/messageService";
import { ApiUrl } from "../../config/apiConfig";
import PhoneInput from "react-phone-input-2";
import {
    isEmptyString,
    validateEmail,
    validatePhoneNumber,
} from "../../services/validationService/validationservice";
import PermissionsGrid from "./PermissionsGrid";
import { sendAccountStatusChangeMail } from "../../functions/accountFunctions";
import InstructorAccessController from "./InstructorAccessController";
import { AdminUser } from "../../models/AdminUser";
import { getEmptyAddUser } from "../../utils/user-utils";

interface AddUserProps {
    isOpen: boolean;
    closeModal: () => void;
    getDataALLs?: () => void;
    openModal: Partial<AdminUser> & { _id?: string };
}


const AddUser: React.FC<AddUserProps> = ({
    isOpen,
    closeModal,
    getDataALLs,
    openModal,
}) => {

    const [formData, setFormData] = useState<AdminUser>(getEmptyAddUser());

    const validatePasswordRules = (password: string) => ({
        length: password.length >= 9 && password.length <= 50,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[&*%^$_*#]/.test(password),
    });

    const [showTooltip, setShowTooltip] = useState(false);
    const [passwordRules, setPasswordRules] = useState(validatePasswordRules(""));
    const [error, setError] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (phone: string) => {
        setFormData((prev) => ({ ...prev, phoneNumber: phone }));
    };

    const togglePermission = (perm: any) => {
        setFormData((prev) => {
            const newPermissions = prev.permissions.includes(perm)
                ? prev.permissions.filter((p) => p !== perm)
                : [...prev.permissions, perm];
            return { ...prev, permissions: newPermissions };
        });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData({ ...formData, [name]: value });

        if (name === "password") {
            setPasswordRules(validatePasswordRules(value));
        }

        setError((prev) => ({ ...prev, [name]: "" }));
    };

    const validateUser = (userForm: AdminUser) => {
        if (!validateEmail(userForm.email)) {
            messageService().toast("error", "please enter valid email");
            return false;
        } else if (!validatePhoneNumber(userForm.phoneNumber)) {
            messageService().toast("error", "please enter valid phone number");
            return false;
        } else if (isEmptyString(userForm.name)) {
            messageService().toast("error", "please enter valid name");
            return false;
        } 

        return true;
    };

    const saveAdmin = async () => {
        try {
            const isValid = validateUser(formData);

            if (!isValid) return;

            const url = openModal?._id ? ApiUrl.updateAdmin + openModal?._id : ApiUrl.createAdmin;
            if (openModal?._id) {
                // update case
                await putData(url, formData);
            } else {
                formData.email = formData.email.toLowerCase();
                // save case
                await postData(url, formData);
                sendAccountStatusChangeMail(
                    formData.email,
                    formData.name,
                    "CONFIRMED",
                    formData.password
                );
            }
            messageService().toast(
                "success",
                `User ${openModal?._id ? "updated" : " created"} successfully`
            );
            getDataALLs?.();
            closeModal();
        } catch (err: any) {
            messageService().toast(
                "error",
                err?.response?.data?.message ?? "error while creating user"
            );
            console.error(err);
        }
    };

    useEffect(() => {
        if (openModal.permissions && openModal._id) {
            setFormData({
                name: openModal.name!,
                email: openModal.email!,
                phoneNumber: openModal.phoneNumber!,
                password: openModal.password!,
                permissions: openModal.permissions,
                adminType: openModal.adminType!,
                coordinators: openModal?.coordinators ?? [],
                tmsPersons: openModal?.tmsPersons ?? [],
                teachers: openModal?.teachers ?? [],
                supportPersons: openModal?.supportPersons ?? [],
                mediaPersons: openModal?.mediaPersons ?? [],
                showAllCoordinators: openModal?.showAllCoordinators ?? false,
                showAllTMSPersons: openModal?.showAllTMSPersons ?? false,
                showAllTeachers: openModal?.showAllTeachers ?? false,
                showAllSupportPersons: openModal?.showAllSupportPersons ?? false,
                showAllMediaPersons: openModal?.showAllMediaPersons ?? false,
                dotPermissions: openModal?.dotPermissions || [],
                hrPermissions: openModal?.hrPermissions || [],
                bizPermissions: openModal?.bizPermissions || [],
                instructorTabButtons: openModal?.instructorTabButtons || [],
                tmsPermissions: openModal?.tmsPermissions || [],
                employeeRef: openModal?.employeeRef || null,
                coursesPermissions:openModal?.coursesPermissions || []
            });
        } else {
            setFormData(getEmptyAddUser());
        }
        console.log(formData);
    }, [openModal, isOpen]);

    const customStyles = {
        content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            padding: 0,
            border: "none",
            overflowY: "auto",
            background: "transparent",
        },
        overlay: {
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 999,
        },
    };

    return (
        //@ts-ignore
        <Modal isOpen={isOpen} onRequestClose={closeModal} style={customStyles}>
            <div className="flex justify-center px-4 font-sans text-textColor min-w-[90vw] md:min-w-[70vw]">
                <div className="bg-gray-50 rounded-lg border shadow-lg w-full">
                    {/* Header */}
                    <div
                        className="flex justify-between shadow-lg  bg-gradient-to-r from-[#007070] via-[#06baba] to-[#007070] 
  bg-[length:200%_200%] animate-[gradient-x_4s_ease_infinite]  text-white px-4 py-2 rounded-t-lg"
                    >
                        <span className="text-sm font-semibold">
                            {openModal?._id ? "Edit" : "Add"} Admin
                        </span>

                        <CircleX
                            className="cursor-pointer bg-white text-greenBg rounded-full"
                            onClick={closeModal}
                        />
                    </div>
                    {/* Form */}
                    <div className="space-y-4 h-[90vh] overflow-y-auto">
                        <div>
                            {formData?.employeeRef &&
                                <div className="grid grid-cols-2 gap-5 mx-4 mt-3">
                                    <div>
                                        <label className="text-sm text-gray-600">Department</label>
                                        <h3>{(formData.employeeRef as any)?.department}</h3>
                                    </div>

                                    <div>
                                        <label className="text-sm text-gray-600">Job Profile</label>
                                        <h3>{(formData.employeeRef as any)?.jobProfile}</h3>
                                    </div>
                                </div>}
                            <div className="flex flex-col md:flex-row">
                                {/* Left Inputs */}
                                <div className="w-full md:w-1/2 p-4 pt-2 space-y-2">
                                    <label className="text-sm text-gray-600">ADMIN NAME</label>
                                    <input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Harman Singh"
                                        className="w-full px-4 py-2 border placeholder:text-[13px] bg-white  placeholder:text-gray-500   leading-[16px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px]  text-gray-800 h-[32px] font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg focus:border-transparent transition-all duration-300"
                                    />

                                    <label className="text-sm text-gray-600">EMAIL ADDRESS</label>
                                    <input
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={openModal?._id !== undefined}
                                        placeholder="info@tms.com"
                                        className={`w-full px-4 py-2 border placeholder:text-[13px]  placeholder:text-gray-500   leading-[16px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-white text-gray-800 h-[32px] font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg focus:border-transparent transition-all duration-300 ${openModal?._id !== undefined && " cursor-not-allowed"
                                            } bg-customGray`}
                                    />
                                </div>

                                {/* Right Inputs */}
                                <div className="w-full md:w-1/2 p-4 pt-2 space-y-2">
                                    <label className="text-sm text-gray-600">PASSWORD</label>
                                    <div className="relative">
                                        <input
                                            name="password"
                                            value={formData.password}
                                            onChange={handlePasswordChange}
                                            disabled={openModal?._id !== undefined}
                                            onFocus={() => setShowTooltip(true)}
                                            onBlur={(e) => {
                                                // close tooltip only if focus leaves input & tooltip
                                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                                    setShowTooltip(false);
                                                }
                                            }}
                                            placeholder="********"
                                            type={showPassword ? "text" : "password"}
                                            className={`w-full px-4 py-2 border placeholder:text-[13px]  placeholder:text-gray-500   leading-[16px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-white text-gray-800 h-[32px] font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg focus:border-transparent transition-all duration-300 ${openModal?._id !== undefined && " cursor-not-allowed"
                                                } rounded-md bg-customGray`}
                                        />
                                        <button
                                            type="button"
                                            tabIndex={-1}
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>

                                        {showTooltip && (
                                            <div className="absolute top-[42px] left-0 w-full bg-white shadow-xl rounded-md p-3 text-[13px] z-20">
                                                <p className="font-semibold mb-1">
                                                    Password must have:
                                                </p>
                                                <ul className="space-y-1">
                                                    <li
                                                        className={
                                                            passwordRules.length
                                                                ? "text-green-600"
                                                                : "text-gray-500"
                                                        }
                                                    >
                                                        ✓ 9–50 characters
                                                    </li>
                                                    <li
                                                        className={
                                                            passwordRules.uppercase
                                                                ? "text-green-600"
                                                                : "text-gray-500"
                                                        }
                                                    >
                                                        ✓ One uppercase
                                                    </li>
                                                    <li
                                                        className={
                                                            passwordRules.lowercase
                                                                ? "text-green-600"
                                                                : "text-gray-500"
                                                        }
                                                    >
                                                        ✓ One lowercase
                                                    </li>
                                                    <li
                                                        className={
                                                            passwordRules.number
                                                                ? "text-green-600"
                                                                : "text-gray-500"
                                                        }
                                                    >
                                                        ✓ One number
                                                    </li>
                                                    <li
                                                        className={
                                                            passwordRules.special
                                                                ? "text-green-600"
                                                                : "text-gray-500"
                                                        }
                                                    >
                                                        ✓ One special (&*%^$_#*)
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <label className="text-sm text-gray-600">PHONE NUMBER</label>
                                        <PhoneInput
                                            inputClass="w-full px-4 py-2 border placeholder:text-[13px]  placeholder:text-gray-500   leading-[16px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] !bg-white text-gray-800 h-[32px] font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg focus:border-transparent transition-all duration-300 "
                                            country={"us"}
                                            value={formData.phoneNumber}
                                            onChange={handlePhoneChange}
                                            disableDropdown
                                            countryCodeEditable={false}
                                        />
                                    </div>
                                    {/* <div className="mt-5">
                                    <label className="text-sm text-gray-600">ADMIN TYPE</label>
                                    <select
                                        className="w-full  border placeholder:text-[13px] placeholder:text-gray-500    border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-white text-gray-800 h-[32px] font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg focus:border-transparent transition-all duration-300"
                                        name="adminType"
                                        value={formData.adminType}
                                        onChange={handleChange}
                                    >
                                        <option value="" disabled selected>
                                            SELECT TYPE
                                        </option>
                                        <option value="Coordinator">Coordinator</option>
                                        <option value="Developer">Developer</option>
                                        <option value="Teacher">Teacher</option>
                                        <option value="Support">Support</option>
                                        <option value="Media">Media</option>
                                    </select>
                                </div> */}
                                </div>
                            </div></div>
                        {/* TODO for now no need Permissions but in future we use */}
                        {/* Permissions */}
                        {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 px-4">
                            {permissionGroups.map((group) => (
                                <Card key={group.title} className="bg-[#F5F5F5] shadow p-2 text-sm">
                                    <CardContent>
                                        <div className="flex gap-2">
                                            <div>
                                                {group.permissions.map((perm) => (
                                                    <Check
                                                        key={perm}
                                                        onClick={() => togglePermission(perm)}
                                                        size={18}
                                                        className={`cursor-pointer mb-1 border rounded-md ${
                                                            formData.permissions &&
                                                            formData.permissions.includes(perm)
                                                                ? "bg-customOrange text-white"
                                                                : "text-gray-400"
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-customOrange mb-1">
                                                    {group.title}
                                                </h3>
                                                {group.permissions.map((perm) => (
                                                    <p key={perm}>{perm}</p>
                                                ))}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div> */}
                        <PermissionsGrid
                            permissions={formData.permissions}
                            setPermissions={(updated) => {
                                setFormData((prev) => ({ ...prev, permissions: updated }));
                            }}
                            formData={formData}
                            setFormData={setFormData}
                        />

                        {/* Footer Buttons */}
                        <div className="flex justify-end px-4 py-2">
                            <button
                                type="button"
                                onClick={saveAdmin}
                                className="bg-customOrange hover:bg-greenBg text-white rounded-md px-4 py-1"
                            >
                                {openModal?._id ? "Update" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddUser;
